/**
 * @fileoverview Responsive info panel that adapts to device type.
 * Renders PopoverInfoCard on desktop, bottom Sheet on mobile/tablet.
 * @module shared/components/ui/ResponsiveInfoPanel
 */

import { ReactElement, ReactNode, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { PopoverInfoCard, type PopoverInfoCardProps } from './PopoverInfoCard'
import { useIsBelowDesktop } from '@/shared/hooks/useIsMobile'
import { cn } from '@/shared/utils/cn'

/**
 * Props for the ResponsiveInfoPanel component.
 */
export interface ResponsiveInfoPanelProps {
  /** Trigger element that opens the panel */
  trigger: ReactNode
  /** Content displayed inside the panel */
  children: ReactNode
  /** Title shown in the sheet header (mobile/tablet only) */
  title: string
  /** Optional description for accessibility */
  description?: string
  /** Preset width for desktop popover: 'one' = 340px, 'two' = 600px */
  size?: PopoverInfoCardProps['size']
  /** Alignment for desktop popover */
  align?: PopoverInfoCardProps['align']
  /** Side for desktop popover */
  side?: PopoverInfoCardProps['side']
  /** Additional class for the sheet content */
  sheetClassName?: string
}

/**
 * Responsive info panel that adapts to device type.
 * - Desktop (≥1048px): Click-triggered popover positioned near trigger
 * - Mobile/Tablet (<1048px): Bottom sheet with swipe-to-dismiss
 *
 * @component
 * @example
 * ```tsx
 * <ResponsiveInfoPanel
 *   trigger={<Button>Show Details</Button>}
 *   title="Sales Details"
 * >
 *   <SalesBreakdown />
 * </ResponsiveInfoPanel>
 * ```
 */
export function ResponsiveInfoPanel({
  trigger,
  children,
  title,
  description,
  size = 'one',
  align = 'center',
  side,
  sheetClassName,
}: ResponsiveInfoPanelProps): ReactElement {
  const isBelowDesktop = useIsBelowDesktop()
  const [isOpen, setIsOpen] = useState(false)

  // Desktop: use PopoverInfoCard (existing behavior)
  if (!isBelowDesktop) {
    return (
      <PopoverInfoCard
        trigger={trigger}
        size={size}
        align={align}
        {...(side && { side })}
      >
        {children}
      </PopoverInfoCard>
    )
  }

  // Mobile/Tablet: use bottom Sheet
  return (
    <>
      {/* Trigger - attach click handler */}
      <div onClick={() => setIsOpen(true)} className="cursor-pointer">
        {trigger}
      </div>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="bottom"
          className={cn(
            'max-h-[85vh] overflow-y-auto rounded-t-2xl',
            'bg-[var(--glass-surface-dark)]',
            sheetClassName
          )}
        >
          {/* Hidden title for accessibility - content has its own visible headers */}
          <SheetHeader className="sr-only">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description ?? title}</SheetDescription>
          </SheetHeader>
          <div className="pb-safe">{children}</div>
        </SheetContent>
      </Sheet>
    </>
  )
}
